import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { ConfirmationDialogComponent } from '@dialogs/confirmation-dialog/confirmation-dialog.component';
import { InlineFormSchemaDialogComponent } from '@dialogs/inline-form-schema-dialog/inline-form-schema-dialog.component';
import { ColorAliases, GridActions, GridOverrides, TeamSettingFormSchema, TeamSettingSection } from '@mds/common';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import {
  ButtonComponent,
  FormSchemaComponent,
  GridActionsComponent,
  GridCellImagePreviewUploadComponent,
  GridModule,
  GridNoRowsComponent,
  TabComponent
} from '@mds/ui';
import { EXTERNAL_VERIFIER_WARNING } from '@pages/dashboards/epd-dashboard/utils/epd-dashboard.model';
import { FileUploadService } from '@services/file-upload.service';
import { TeamAdminApiService } from '@services/team-admin-api.service';
import { ToastrService } from '@services/toaster.service';
import { ColDef, RowNode } from 'ag-grid-enterprise';
import isEqual from 'lodash/isEqual';

interface ExternalVerifierRow {
  code: string;
  company_name: string;
  verifier_name: string;
  signature_image_rid: string;
  signature_image_url: string;
  signature_image_url_with_token: string;
  signature_image_filename: string;
  actions?: { edit: boolean; delete: boolean };
}

interface ExternalVerifierFormValue {
  [key: string]: unknown;
}

interface FormValueChangeEvent {
  valid: boolean;
  value: Record<string, unknown>;
  form: UntypedFormGroup;
}

const EPD_LOGO_FIELD_KEY = 'epd_company_logo_url';
const EXTERNAL_VERIFIER_COMPANY_NAME_FIELD_KEY = 'company_name';
const EXTERNAL_VERIFIER_NAME_FIELD_KEY = 'verifier_name';
const EXTERNAL_VERIFIER_CODE_FIELD_KEY = 'verifier_code';
const NORGE_GROUP_NAME = 'NORGE';

interface ConfigurationTab {
  label: string;
}

export type EpdWrapperTab = 'EPD' | 'Color Palette';

export interface EpdSectionChange {
  tab: EpdWrapperTab;
  section: TeamSettingSection;
}

type UpdatableTeamSettingSection = TeamSettingSection & {
  isUpdated?: boolean;
};

const VERIFIER_DIALOG_SCHEMA: TeamSettingFormSchema[] = [
  {
    key: EXTERNAL_VERIFIER_CODE_FIELD_KEY,
    title: 'Code',
    description: 'Code',
    mandatory: true,
    defaultValue: null,
    formtype: 'input',
    section: 'Inputs',
    fieldtype: 'string',
    enum: null,
    sortlevel: 1
  },
  {
    key: EXTERNAL_VERIFIER_COMPANY_NAME_FIELD_KEY,
    title: 'Company name',
    description: 'Company name',
    mandatory: true,
    defaultValue: null,
    formtype: 'input',
    section: 'Inputs',
    fieldtype: 'string',
    enum: null,
    sortlevel: 2
  },
  {
    key: EXTERNAL_VERIFIER_NAME_FIELD_KEY,
    title: 'Verifier name',
    description: 'Verifier name',
    mandatory: true,
    defaultValue: null,
    formtype: 'input',
    section: 'Inputs',
    fieldtype: 'string',
    enum: null,
    sortlevel: 3
  }
];

function getFieldGroup(field: TeamSettingFormSchema): string {
  return field.group || field.section;
}

function getExternalVerifierFieldKey(groupLabel: string): string {
  return `epd_${groupLabel.toLowerCase()}_external_verifiers`;
}

function getUsageModeFieldKey(groupLabel: string): string {
  return `epd_${groupLabel.toLowerCase()}_epd_usage_mode`;
}

@Component({
  selector: 'app-epd-settings',
  standalone: true,
  imports: [ButtonComponent, FormSchemaComponent, GridModule, MatDialogModule, MdsIconsModule, TabComponent],
  templateUrl: './epd-settings.component.html',
  styleUrls: ['./epd-settings.component.scss']
})
export class EpdSettingsComponent implements OnInit {
  @Input() teamRid!: string;
  @Input() section!: TeamSettingSection;
  @Input() wrapperTab!: EpdWrapperTab;
  @Output() sectionChange = new EventEmitter<EpdSectionChange>();
  public readonly icons = MdsIcon;
  public readonly colorAliases = ColorAliases;
  public readonly externalVerifierWarning = EXTERNAL_VERIFIER_WARNING;
  public epdSectionSchema: UpdatableTeamSettingSection | null = null;
  public epdFormSection: UpdatableTeamSettingSection | null = null;
  public epdFormData: Record<string, unknown> | null = null;
  public activeConfigurationForm: UntypedFormGroup = new UntypedFormGroup({});
  public generalForm: UntypedFormGroup = new UntypedFormGroup({});
  public verifierGridOverrides: GridOverrides;
  public externalVerifiers: ExternalVerifierRow[] = [];
  private epdDisplayData: Record<string, unknown> = {};
  public configurationTabs: ConfigurationTab[] = [];
  public activeConfigurationTab: ConfigurationTab | null = null;
  public activeConfigurationSections: TeamSettingSection[] = [];
  public configurationActions: TeamSettingSection['actions'] | null = null;
  private teamAdminApiService = inject(TeamAdminApiService);
  private fileUploadService = inject(FileUploadService);

  private toastr = inject(ToastrService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    const epdSection = this.section;

    this.epdFormSection = epdSection
      ? {
          ...epdSection,
          formSchema: epdSection.formSchema.filter((field) => ![EPD_LOGO_FIELD_KEY].includes(field.key) && !field.group)
        }
      : null;

    this.epdSectionSchema = this.epdFormSection;
    const sectionFormData = this.getSectionFormData(epdSection);
    this.epdFormData = this.getUpdatableFormData(sectionFormData);
    this.epdDisplayData = this.getSectionDisplayData(epdSection);
    this.configurationTabs = this.getConfigurationTabs(epdSection);
    this.activeConfigurationTab = this.configurationTabs[0] ?? null;
    // Initialize the grid from the first active tab's display_value (has urls and filenames).
    this.externalVerifiers = this.getExternalVerifiers(
      this.epdDisplayData?.[
        this.activeConfigurationTab ? getExternalVerifierFieldKey(this.activeConfigurationTab.label) : ''
      ]
    );
    this.configurationActions = epdSection?.actions ?? null;
    this.updateActiveConfigurationSection();
    this.updateVerifierGrid();
  }

  configurationFormValueChanged(ev: FormValueChangeEvent): void {
    this.mergeFormValue(ev, this.activeConfigurationSections[0]?.name);
  }

  generalFormValueChanged(ev: FormValueChangeEvent): void {
    this.mergeFormValue(ev, this.epdFormSection?.name);
  }

  private mergeFormValue(ev: FormValueChangeEvent, sectionName?: string): void {
    if (!ev.valid) return;

    const sectionValue = sectionName ? ev.value[sectionName] : null;
    const updatedValue =
      sectionValue && typeof sectionValue === 'object' && !Array.isArray(sectionValue)
        ? (sectionValue as Record<string, unknown>)
        : {};
    const nextFormData = {
      ...(this.epdFormData || {}),
      ...updatedValue
    };

    if (ev.form.dirty && this.epdFormData && !isEqual(this.epdFormData, nextFormData)) {
      this.epdSectionSchema.isUpdated = true;
    }
    this.epdFormData = nextFormData;
  }

  submitEpdForm() {
    if (!this.epdSectionSchema.isUpdated) {
      return;
    }

    this.configurationFormValueChanged({
      valid: this.activeConfigurationForm.valid,
      value: this.activeConfigurationForm.getRawValue(),
      form: this.activeConfigurationForm
    });
    this.generalFormValueChanged({
      valid: this.generalForm.valid,
      value: this.generalForm.getRawValue(),
      form: this.generalForm
    });
    this.updateSettings(this.epdFormData);
  }

  updateSettings(formData: Record<string, unknown>) {
    const payload = {
      key: this.teamRid,
      formData
    };
    this.teamAdminApiService.updateTeamSetting(this.teamRid, payload).subscribe({
      next: (response: { updated?: Record<string, unknown> }) => {
        const updatedValues = response.updated ?? {};
        const updatedLines = this.section.lines.map((line) => {
          if (Array.isArray(line)) {
            return line.map((item) => {
              const updatedValue = updatedValues[item.key] as typeof item.value;

              return Object.prototype.hasOwnProperty.call(updatedValues, item.key)
                ? { ...item, value: updatedValue, display_value: updatedValue }
                : item;
            });
          }

          const updatedValue = updatedValues[line.key] as typeof line.value;

          return Object.prototype.hasOwnProperty.call(updatedValues, line.key)
            ? { ...line, value: updatedValue, display_value: updatedValue }
            : line;
        });
        const refreshedSectionName = `${this.section.name} `;

        this.section = {
          ...this.section,
          name: refreshedSectionName,
          lines: updatedLines
        };
        this.epdFormSection = {
          ...this.epdFormSection,
          name: refreshedSectionName,
          lines: updatedLines
        };
        this.epdSectionSchema = this.epdFormSection;
        this.updateActiveConfigurationSection();
        this.sectionChange.emit({ tab: this.wrapperTab, section: this.section });
        this.toastr.showMessage('Updated Successfully', 2000);
        if (this.epdSectionSchema?.isUpdated) {
          this.epdSectionSchema.isUpdated = false;
        }
        this.activeConfigurationForm.markAsPristine();
        this.generalForm.markAsPristine();
      },
      error: () => {
        this.toastr.showMessage('Failed to update EPD settings', 2000);
      }
    });
  }

  gridActionsEvent(params: { event: GridActions; node: RowNode }) {
    switch (params.event) {
      case GridActions.EDIT:
        this.editVerifier(params.node.data);
        break;
      case GridActions.DELETE:
        this.deleteVerifier(params.node.data);
        break;
      default:
        break;
    }
  }

  private uploadVerifierSignature(row: ExternalVerifierRow, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.fileUploadService
      .newFileUpload(this.teamRid.replace('#', ''), file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: Record<string, { file_rid?: string }>) => {
          const fileRid = Object.values(res)?.[0]?.file_rid || '';
          const updatedRows = this.externalVerifiers.map((verifier) =>
            verifier.code === row.code
              ? { ...verifier, signature_image_rid: fileRid, signature_image_filename: file.name }
              : verifier
          );
          this.persistExternalVerifiers(updatedRows);
        },
        error: () => {
          this.toastr.showMessage('Failed to upload signature image', 5000);
        }
      });
  }

  private removeVerifierSignature(row: ExternalVerifierRow): void {
    const updatedRows = this.externalVerifiers.map((verifier) =>
      verifier.code === row.code
        ? {
            ...verifier,
            signature_image_rid: '',
            signature_image_url: '',
            signature_image_url_with_token: '',
            signature_image_filename: ''
          }
        : verifier
    );
    this.persistExternalVerifiers(updatedRows);
  }

  configurationTabChange(tab: ConfigurationTab) {
    this.activeConfigurationTab = tab;
    // Each tab reads and writes its own verifier JSON blob.
    this.externalVerifiers = this.getExternalVerifiers(this.epdDisplayData?.[getExternalVerifierFieldKey(tab.label)]);
    this.updateActiveConfigurationSection();
    this.updateVerifierGrid();
  }

  addNewVerifier(): void {
    if (!this.canEditVerifiers()) {
      return;
    }

    this.openVerifierDialog(
      { company_name: '', verifier_name: '', code: '' },
      'New External Verifier',
      (updatedVerifier) =>
        this.persistExternalVerifiers([
          ...this.externalVerifiers,
          {
            ...updatedVerifier,
            signature_image_rid: '',
            signature_image_url: '',
            signature_image_url_with_token: '',
            signature_image_filename: ''
          }
        ])
    );
  }

  private editVerifier(row: ExternalVerifierRow): void {
    if (!this.canEditVerifiers()) {
      return;
    }

    this.openVerifierDialog(row, 'Edit Verifier', (updatedVerifier) => {
      const updatedRows = this.externalVerifiers.map((verifier) =>
        verifier.verifier_name === row.verifier_name && verifier.code === row.code
          ? { ...verifier, ...updatedVerifier }
          : verifier
      );

      this.persistExternalVerifiers(updatedRows);
    });
  }

  private deleteVerifier(row: ExternalVerifierRow): void {
    if (!this.canDeleteVerifiers()) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: 'Remove verifier?',
        text: `Remove "${row.verifier_name}" (${row.company_name}) with code "${row.code}" from the list of external verifiers? Click Update to save this change.`,
        cancel_text: 'Cancel',
        delete_text: 'Delete'
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (!confirmed) return;

        const updatedRows = this.externalVerifiers.filter(
          (verifier) => !(verifier.verifier_name === row.verifier_name && verifier.code === row.code)
        );
        this.persistExternalVerifiers(updatedRows);
      });
  }

  private persistExternalVerifiers(rows: ExternalVerifierRow[]): void {
    this.externalVerifiers = rows.map((verifier) => ({
      ...verifier,
      actions: { edit: true, delete: true }
    }));
    this.updateVerifierGrid();

    const activeTabKey = this.activeConfigurationTab?.label.toLowerCase();
    if (activeTabKey) {
      // Persist only the value fields (what the BE expects)
      const valueRows = rows.map(({ code, company_name, verifier_name, signature_image_rid }) => ({
        code,
        company_name,
        verifier_name,
        signature_image_rid: signature_image_rid || ''
      }));
      const externalVerifierValue = JSON.stringify(valueRows);

      const externalVerifierFieldKey = getExternalVerifierFieldKey(this.activeConfigurationTab.label);

      this.epdFormData = {
        ...(this.epdFormData || {}),
        [externalVerifierFieldKey]: externalVerifierValue
      };
      this.epdDisplayData = {
        ...(this.epdDisplayData || {}),
        [externalVerifierFieldKey]: rows
      };
      this.epdSectionSchema.isUpdated = true;
    }
  }

  private updateVerifierGrid(): void {
    const isNorge = this.activeConfigurationTab?.label === NORGE_GROUP_NAME;

    const columnDefs: ColDef[] = [
      {
        field: 'code',
        headerName: 'Code',
        minWidth: 140
      },
      {
        field: 'company_name',
        headerName: 'Company name',
        minWidth: 180
      },
      ...(isNorge
        ? [
            {
              field: 'verifier_name',
              headerName: 'Verifier name',
              minWidth: 180
            },
            {
              headerName: 'Signature',
              field: 'signature_image_url',
              minWidth: 160,
              cellRenderer: GridCellImagePreviewUploadComponent,
              cellRendererParams: {
                imageUrlField: 'signature_image_url_with_token',
                filenameField: 'signature_image_filename',
                altTextField: 'signature_image_filename',
                onUpload: (node: RowNode, event: Event) => {
                  this.uploadVerifierSignature(node.data, event);
                },
                onRemove: (node: RowNode) => {
                  this.removeVerifierSignature(node.data);
                }
              }
            }
          ]
        : []),
      {
        headerName: 'Actions',
        field: 'actions',
        lockPosition: true,
        pinned: 'right',
        width: 120,
        cellRenderer: GridActionsComponent,
        cellRendererParams: {
          onClick: (params: { event: GridActions; node: RowNode }) => {
            this.gridActionsEvent(params);
          }
        }
      }
    ];

    this.verifierGridOverrides = {
      columnDefs,
      rowData: this.shouldShowVerifierGrid() ? this.externalVerifiers : [],
      defaultColDef: {
        flex: 1,
        minWidth: 120,
        sortable: true,
        filter: false,
        resizable: true,
        suppressHeaderMenuButton: true
      },
      noRowsOverlayComponent: GridNoRowsComponent,
      noRowsOverlayComponentParams: {
        title: 'No external verifiers configured'
      }
    };

    if (!this.canEditVerifiers() || !this.canDeleteVerifiers()) {
      this.verifierGridOverrides = {
        ...this.verifierGridOverrides,
        rowData: this.verifierGridOverrides.rowData?.map((row: ExternalVerifierRow) => ({
          ...row,
          actions: {
            edit: this.canEditVerifiers(),
            delete: this.canDeleteVerifiers()
          }
        }))
      };
    }
  }

  private getSectionFormData(section: TeamSettingSection): Record<string, unknown> {
    const formData: Record<string, unknown> = {};

    section.lines.forEach((line) => {
      if (Array.isArray(line)) {
        line.forEach((item) => {
          formData[item.key] = Array.isArray(item.value) ? JSON.stringify(item.value) : item.value;
        });
        return;
      }

      formData[line.key] = Array.isArray(line.value) ? JSON.stringify(line.value) : line.value;
    });

    return formData;
  }

  private getSectionDisplayData(section: TeamSettingSection): Record<string, unknown> {
    const displayData: Record<string, unknown> = {};

    section.lines.forEach((line) => {
      if (Array.isArray(line)) {
        line.forEach((item) => {
          displayData[item.key] = item.display_value;
        });
        return;
      }

      displayData[line.key] = line.display_value;
    });

    return displayData;
  }

  private getUpdatableFormData(sectionFormData: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(Object.entries(sectionFormData).filter(([key]) => key !== EPD_LOGO_FIELD_KEY));
  }

  private getExternalVerifiers(value: unknown): ExternalVerifierRow[] {
    let parsedValue = value;

    if (typeof value === 'string') {
      try {
        parsedValue = JSON.parse(value);
      } catch {
        return [];
      }
    }

    if (!Array.isArray(parsedValue)) {
      return [];
    }

    return parsedValue
      .filter((item) => item && typeof item === 'object' && typeof item.code === 'string')
      .map((item) => ({
        code: item.code,
        company_name: item.company_name || '',
        verifier_name: item.verifier_name || '',
        signature_image_rid: item.signature_image_rid || '',
        signature_image_url: item.signature_image_url || '',
        signature_image_url_with_token: item.signature_image_url_with_token || '',
        signature_image_filename: item.signature_image_filename || '',
        actions: { edit: true, delete: true }
      }));
  }

  private updateActiveConfigurationSection(): void {
    if (!this.section || !this.activeConfigurationTab) {
      this.activeConfigurationSections = [];
      return;
    }

    // Verifier dialog fields stay out of the visible form and only power the grid/dialog.
    const activeFields = this.section.formSchema.filter(
      (field) => getFieldGroup(field) === this.activeConfigurationTab?.label && !this.isVerifierGridField(field)
    );

    if (!activeFields.length) {
      this.activeConfigurationSections = [];
      return;
    }

    const activeFieldKeys = new Set(activeFields.map((field) => field.key));
    const activeLines = this.section.lines.filter((line) => {
      if (Array.isArray(line)) {
        return line.some((item) => activeFieldKeys.has(item.key));
      }

      return activeFieldKeys.has(line.key);
    });

    this.activeConfigurationSections = [
      {
        ...this.section,
        name: `${this.section.name} - ${this.activeConfigurationTab.label}`,
        lines: activeLines,
        formSchema: activeFields
      }
    ];
  }

  public shouldShowVerifierGrid(): boolean {
    if (!this.section || !this.activeConfigurationTab) {
      return false;
    }

    const externalVerifierFieldKey = getExternalVerifierFieldKey(this.activeConfigurationTab.label);

    return this.section.formSchema.some((field) => field.key === externalVerifierFieldKey);
  }

  public shouldShowExternalVerifierWarning(): boolean {
    const activeTabLabel = this.activeConfigurationTab?.label;
    if (!activeTabLabel) {
      return false;
    }

    const usageMode = this.epdFormData?.[getUsageModeFieldKey(activeTabLabel)];
    return ['external', 'both'].includes(usageMode as string) && this.externalVerifiers.length === 0;
  }

  public canEditVerifiers(): boolean {
    return this.configurationActions?.edit !== false;
  }

  public canDeleteVerifiers(): boolean {
    return this.configurationActions?.delete !== false;
  }

  private isVerifierGridField(field: TeamSettingFormSchema): boolean {
    return (
      [
        EXTERNAL_VERIFIER_COMPANY_NAME_FIELD_KEY,
        EXTERNAL_VERIFIER_NAME_FIELD_KEY,
        EXTERNAL_VERIFIER_CODE_FIELD_KEY
      ].some((prefix) => field.key.startsWith(prefix)) || field.key.endsWith('_external_verifiers')
    );
  }

  private openVerifierDialog(
    row: Pick<ExternalVerifierRow, 'company_name' | 'verifier_name' | 'code'>,
    header: string,
    onSave: (updatedVerifier: { company_name: string; verifier_name: string; code: string }) => void
  ): void {
    const activeTabKey = this.activeConfigurationTab?.label.toLowerCase();
    const isNorge = this.activeConfigurationTab?.label === NORGE_GROUP_NAME;

    if (!activeTabKey) {
      return;
    }

    const companyNameFieldKey = EXTERNAL_VERIFIER_COMPANY_NAME_FIELD_KEY;
    const nameFieldKey = EXTERNAL_VERIFIER_NAME_FIELD_KEY;
    const codeFieldKey = EXTERNAL_VERIFIER_CODE_FIELD_KEY;

    const dialogRef = this.dialog.open(InlineFormSchemaDialogComponent, {
      autoFocus: false,
      data: {
        header,
        confirmText: 'Save',
        formSchema: isNorge
          ? VERIFIER_DIALOG_SCHEMA
          : VERIFIER_DIALOG_SCHEMA.filter((field) => field.key !== EXTERNAL_VERIFIER_NAME_FIELD_KEY),
        lines: [
          {
            key: codeFieldKey,
            value: row.code,
            display_value: row.code,
            label: 'Code'
          },
          {
            key: companyNameFieldKey,
            value: row.company_name,
            display_value: row.company_name,
            label: 'Company name'
          },
          {
            key: nameFieldKey,
            value: row.verifier_name,
            display_value: row.verifier_name,
            label: 'Verifier name'
          }
        ]
      }
    });

    dialogRef.componentInstance.submitDataEmitter
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ valid, value }) => {
        if (!valid) {
          return;
        }

        const [firstValue] = Object.values(value);
        const verifierValues =
          firstValue && typeof firstValue === 'object' && !Array.isArray(firstValue)
            ? (firstValue as ExternalVerifierFormValue)
            : {};
        const updatedVerifier = {
          company_name:
            typeof verifierValues[companyNameFieldKey] === 'string'
              ? (verifierValues[companyNameFieldKey] as string).trim()
              : '',
          verifier_name:
            typeof verifierValues[nameFieldKey] === 'string' ? (verifierValues[nameFieldKey] as string).trim() : '',
          code: typeof verifierValues[codeFieldKey] === 'string' ? (verifierValues[codeFieldKey] as string).trim() : ''
        };

        dialogRef.close(true);
        onSave(updatedVerifier);
      });
  }

  private getConfigurationTabs(section?: TeamSettingSection): ConfigurationTab[] {
    if (!section?.formSchema?.length) {
      return [];
    }

    return section.formSchema
      .reduce((tabs: ConfigurationTab[], field: TeamSettingFormSchema) => {
        if (!field.group) {
          return tabs;
        }

        const fieldGroup = getFieldGroup(field);

        if (!fieldGroup || tabs.some((tab) => tab.label === fieldGroup)) {
          return tabs;
        }

        return [...tabs, { label: fieldGroup }];
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }
}
